import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { validateLogin } from "../../services/validate-login";
import Header from "../../components/common/Header";
import Footer from "../../components/common/Footer";
import { getFormattedDate } from "../../utils/dateFormat";
import { getSelect3ResponseService, hlKycService } from "../../services/ondcService";
import Loader from "../../components/common/Loader";
import { webSocketURL } from "../../services/endpointUrl";

function Form() {
  const [formIsComplete, setFormIsComplete] = useState(false); 
  const [loading, setLoading] = useState(true); 
  const [hlKycUrl, setHlKycUrl] = useState("");
  const [hlKycSession, setHlKycSession] = useState("");
  const [popup, setPopup] = useState<Window | null>(null);
  const [messages, setMessages] = useState([]);
  const [socket, setSocket] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    async function getStep() {
      if(Number(localStorage.getItem("step")) >= 7){} else{ navigate("/personal-loan") }
    }
    getStep();
  }, []);

  const popupRef = useRef<Window | null>(null);
  useEffect(() => {
    async function fetchOffers() {
      try {
        // const response = {success: true,message: {code: 200,timestamp: 1756458547022,data: {"@entity": "in.co.sandbox.kyc.digilocker.session.response",authorization_url:"https://google.com/",session_id: "c7a342c5-d67f-46c0-92f5-b28706d6f3be",},transaction_id: "8a510a6f-bfda-4bc4-84fc-d0e756bc5afe",},};
        const response = await hlKycService(localStorage.getItem("transaction_id")); console.log(response)
        if (response.success) {
          setHlKycUrl(response.message.data.authorization_url);
          setHlKycSession(response.message.data.session_id);

          // Create WebSocket connection
          const ws = new WebSocket(webSocketURL);

          ws.onopen = () => {
            console.log("✅ Connected to WebSocket server");
            ws.send(response.message.data.session_id + "-aa-consent");
          };

          ws.onmessage = (event) => {
            try {
              const ack = JSON.parse(event.data);
              console.log("📩 WS message:", ack);

              if (ack.success) {

                // Close popup if it's open
                if (popupRef.current && !popupRef.current.closed) {
                  popupRef.current.close();
                }
                popupRef.current = null;
                setFormIsComplete(true);

              } else{
                // Close popup if it's open
                if (popupRef.current && !popupRef.current.closed) {
                  popupRef.current.close();
                }else {
                }
                popupRef.current = null;
                navigate("/sorry");
              }
            } catch (e) {
              console.error("Invalid WS data:", event.data);
            }
          };

          ws.onerror = (err) => {
            console.error("❌ WebSocket error:", err);
          };

          ws.onclose = () => {
            console.log("🔌 Disconnected from WebSocket server");
          };

          // Cleanup on component unmount
          return () => ws.close();
        }
      } catch (err) {
        console.error("Error fetching offers:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchOffers();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#f5f9ff]">
        <Loader />
      </div>
    );
  }

  const openPopup = () => {
    // Take 80% of screen size
    const width = Math.floor(window.screen.width * 0.8);
    const height = Math.floor(window.screen.height * 0.8);

    // Center the popup
    const left = Math.floor((window.screen.width - width) / 2);
    const top = Math.floor((window.screen.height - height) / 2);

    const features = `width=${width},height=${height},left=${left},top=${top},resizable,scrollbars`;

    popupRef.current = window.open(
      hlKycUrl,
      "popupWindow",
      features
    );
  };

  const onClickEvent = () => {
    navigate("/get-lenders-kyc");
  };

  return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[#f5f9ff] px-4">
        {/* Hero Section */}
      <div className="flex flex-col md:flex-row max-w-5xl w-full bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Left side Image (fixed) */}
        <div className="basis-full md:basis-2/5 flex items-center justify-center p-6 bg-gray-50 md:h-[calc(100vh-100px)] md:sticky md:top-0">
          <img
            src="svg/img3.svg"
            alt="Working professional"
            className="w-full max-w-xs md:max-w-none object-contain"
          />
        </div>

        {/* Right side Form (scrollable) */}
        <div className="basis-full md:basis-3/5 flex flex-col p-8 bg-white relative md:h-[calc(100vh-100px)] md:overflow-y-auto">
          {/* Step Number */}
          <div className="absolute top-4 right-4 bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
            Step 8
          </div>
        <div className="flex-1 overflow-y-auto">
          <h2 className="text-xl font-semibold mb-6">Get KYC Done</h2>
          <p className="text-sm text-gray-600 mb-4"><b>You will be directed for HL KYC</b></p>
          <p className="text-sm text-gray-600 mb-4"><b>Once HL KYC is successful, you will be directed to Lender’s KYC</b></p>
        </div>
        

        {/* Buttons */}
        <div className="mt-6">
          <button className={`w-full px-8 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition
            ${formIsComplete 
              ? "bg-gray-400 cursor-not-allowed" 
              : "bg-blue-600 text-white hover:bg-blue-700"}`}
          onClick={() => openPopup()}
          disabled={formIsComplete}

          >
            Get HL KYC
          </button><br></br>
          <button className={`w-full px-8 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition
            ${!formIsComplete 
              ? "bg-gray-400 cursor-not-allowed" 
              : "bg-blue-600 text-white hover:bg-blue-700"}`}
            onClick={() => {
              localStorage.setItem("step", "8")
              navigate("/get-lenders-kyc")}}
            disabled={!formIsComplete}
            >
              Next <ArrowRight size={18} />
            </button>
        </div>
      </div>
        </div>
  
        <Footer />
      </div>
  );
}

export default function Step7HLKycPage() {
  const navigate = useNavigate();

  useEffect(() => {
    validateLogin(navigate);
  }, [navigate]);

  return (
    <div className="flex flex-col h-screen overflow-y-auto">
      <div className="flex-shrink-0">
        <Header title="" />
      </div>

      <main className="flex-1 overflow-y-auto">
        <Form />
      </main>
    </div>
  );
}
